import { type FC, type JSX } from "react";
import { useSelector } from "react-redux";
import { CardGridArea } from "widgets";

import { renderVariantAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";

import { type User } from "../../graphql.js";
import { type IMutationState } from "../../redux/modules/mutation.js";
import { type IAppState } from "../../redux/reducer.js";
import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import { ContigLocationCard } from "./cards/ContigLocationCard.js";
import GeneMutationsCard from "./cards/GeneMutationsCard.js";
import { GenomeCard } from "./cards/GenomeCard.js";
import ProteinMutationsCard from "./cards/ProteinMutationsCard.js";
import RelatedProjectsCard from "./cards/RelatedProjectsCard.js";
import { VariantCard } from "./cards/VariantCard.js";
import GenericEntitySummaryView from "./GenericEntitySummaryView.js";

interface IVariantSummaryViewOwnProps {
  title?: string;
  headerTags?: JSX.Element[];
}

const VariantSummaryView: FC<IVariantSummaryViewOwnProps> = (props) => {
  const { mutation, isLoading } = useSelector<IAppState, IMutationState>((state) => state.mutation);
  const currentUser = useSelector<IAppState, User>((state) => state.auth.session);

  const showSpinner = isLoading || !mutation;
  const variant = mutation?.refVariant;

  return (
    <GenericEntitySummaryView
      title={(props.title ? `${props.title}: ` : "") + (variant ? variant.name : "")}
      subtitle={variant?.description || "Variant Summary"}
      entity={mutation}
      refGenomes={mutation?.refVariant.variantAnalysisGroup.refGenomes}
      headerTags={props.headerTags}
      headerAttributes={[
        <AttributesListWidget
          attributes={renderVariantAttributesWidgets(variant)}
          key={"variant-summary-view-widget-1"}
        />,
      ]}
      isLoading={showSpinner}
      getEntityName={() => variant?.name}
    >
      {mutation && (
        <>
          <VariantCard variant={mutation.refVariant} area={CardGridArea.Left} />
          <GenomeCard title="Reference Genome" genome={mutation.refVariant.refGenome} area={CardGridArea.Left} />
          <ContigLocationCard
            label="Reference Location"
            region={mutation.refVariant.region}
            lowerNeighborGenes={mutation.refVariant.lowerGenes}
            higherNeighborGenes={mutation.refVariant.higherGenes}
            lowerNeighborVariants={mutation.refVariant.lowerVariants}
            higherNeighborVariants={mutation.refVariant.higherVariants}
            variant={mutation.refVariant}
            area={CardGridArea.Right}
          />
          <GeneMutationsCard
            geneMutations={mutation.refVariant.geneMutations}
            ignoreIfEmpty={false}
            title={"Reference Genes"}
            area={CardGridArea.Right}
          />
          <ProteinMutationsCard
            proteinMutations={mutation.refVariant.proteinMutations}
            ignoreIfEmpty={false}
            title={"Reference Proteins"}
            area={CardGridArea.Right}
          />
          <RelatedProjectsCard
            relatedProjects={mutation.relatedProjects}
            currentUser={currentUser}
            area={CardGridArea.Panel}
          />
        </>
      )}
    </GenericEntitySummaryView>
  );
};

export default VariantSummaryView;
